/**
 * Live Stats Panel Component
 * Real-time metrics display
 */

import { Card } from '../ui';
import { Users, Truck, Package, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { cn } from '../../utils/cn';

interface LiveStatsPanelProps {
  stats: {
    totalDrivers: number;
    activeDrivers: number;
    totalRoutes: number;
    completedStops: number;
    pendingStops: number;
    delayedStops: number;
  };
  isLoading?: boolean;
}

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'yellow' | 'red' | 'gray';
  trend?: { value: number; isPositive: boolean };
}

function StatCard({ label, value, icon, color, trend }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
    gray: 'bg-gray-50 text-gray-600',
  };

  return (
    <Card padding="sm">
      <div className="flex items-center gap-3">
        <div className={cn('p-2 rounded-lg', colorClasses[color])}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
        {trend && (
          <div
            className={cn(
              'ml-auto text-xs font-medium',
              trend.isPositive ? 'text-green-600' : 'text-red-600'
            )}
          >
            {trend.isPositive ? '+' : ''}{trend.value}%
          </div>
        )}
      </div>
    </Card>
  );
}

export default function LiveStatsPanel({ stats, isLoading }: LiveStatsPanelProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {[...Array(6)].map((_, i) => (
          <Card key={i} padding="sm">
            <div className="animate-pulse flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded-lg" />
              <div>
                <div className="h-6 w-12 bg-gray-200 rounded mb-1" />
                <div className="h-3 w-16 bg-gray-200 rounded" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  const activePercentage = stats.totalDrivers > 0
    ? Math.round((stats.activeDrivers / stats.totalDrivers) * 100)
    : 0;

  const completionPercentage = (stats.completedStops + stats.pendingStops) > 0
    ? Math.round((stats.completedStops / (stats.completedStops + stats.pendingStops)) * 100)
    : 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
      <StatCard
        label="Drivers on map"
        value={stats.totalDrivers}
        icon={<Users className="h-5 w-5" />}
        color="blue"
      />
      <StatCard
        label="Active Now"
        value={`${stats.activeDrivers} (${activePercentage}%)`}
        icon={<Truck className="h-5 w-5" />}
        color="green"
      />
      <StatCard
        label="Active Routes"
        value={stats.totalRoutes}
        icon={<Package className="h-5 w-5" />}
        color="blue"
      />
      <StatCard
        label="Completed"
        value={`${stats.completedStops} (${completionPercentage}%)`}
        icon={<CheckCircle className="h-5 w-5" />}
        color="green"
      />
      <StatCard
        label="Pending"
        value={stats.pendingStops}
        icon={<Clock className="h-5 w-5" />}
        color="yellow"
      />
      <StatCard
        label="Delayed"
        value={stats.delayedStops}
        icon={<AlertTriangle className="h-5 w-5" />}
        color={stats.delayedStops > 0 ? 'red' : 'gray'}
      />
    </div>
  );
}
